/**
 * The media gallery.
 *
 * Every photograph here is a real photograph of the Group, its people or its
 * installations — nothing generic, nothing bought. Each entry records where the
 * image comes from, so a journalist can trace it, and what it actually shows,
 * so nobody has to guess.
 *
 * `source` is the public page the image is published on. `entity` is the
 * operating company it belongs to. Items are listed newest and most significant
 * first; the anniversary photograph is the feature.
 */

export const MEDIA_GALLERY = [
  {
    id: 'afli-signing',
    assetKey: 'media/afli-signing-15-signing-group',
    entity: 'Supratechnic',
    date: '2026-10-14',
    title: 'Afli — the GreenFish collaboration, signed in Kuala Lumpur',
    alt:
      'The two signatories seated at the signing table with five colleagues standing behind them, in front of a backdrop reading “Supratechnic × GreenFish — Collaboration Agreement Signing Ceremony”.',
    context:
      'Supratechnic signed a Collaboration Agreement with Icelandic fisheries technology company GreenFish hf. on 14 October 2026 and becomes the exclusive distributor of Afli — an AI-powered fishing app for small-scale fishermen — in Malaysia, Singapore and Indonesia. A free pilot for 50 fishermen runs through the monsoon ahead of a commercial launch across the seven Malaysian branches. Fourteen photographs from the ceremony, and GreenFish’s walkthrough of the app, are published with the release.',
    caption: 'Mercure Kuala Lumpur Trion, 14 October 2026.',
    source: '/newsroom/2026-10-14-suprai-supratechnic-greenfish-afli-agreement',
    sourceLabel: 'The press release, with the full gallery',
    credit: 'USP Group',
  },
  {
    id: 'igd-sii-signing',
    assetKey: 'media/igd-sii-signing',
    entity: 'SII',
    date: '2026-09-29',
    title: 'IGD appoints SII its authorised distributor in Singapore',
    alt:
      'Mr Melvin Tan Wei Yang and Mr Uu Su Haw seated at a boardroom table signing the Distributor and Channel Partner Agreement, with four USP Group directors and officers standing behind them.',
    context:
      'International Gas Detectors appointed SII its authorised distributor and channel partner in Singapore, formalised through a Distributor and Channel Partner Agreement signed on 29 September 2026 for an initial term of thirty-six months. It covers the promotion, sale, installation, servicing and calibration support of IGD’s UK-engineered fixed gas detection systems across Singapore, and pairs them with SII’s SIICAL calibration laboratory and validation services.',
    caption:
      'From left: Mr Toh Hai Joo, Independent Non-Executive Director, USP Group Limited; Mr Chan Chung Khang, Independent Non-Executive Chairman, USP Group Limited; Mr Melvin Tan Wei Yang, Chief Executive Officer, Scientific & Industrial Instrumentation Pte. Ltd. and Executive Director & CEO, USP Group Limited; Mr Uu Su Haw, President, Asia Pacific, Safe Monitoring Group; Mr Leow Yong Kin, Chief Financial Officer, USP Group Limited; and Ms Maio Dangi, Non-Independent Non-Executive Deputy Chairperson, USP Group Limited, at the signing of the Distributor and Channel Partner Agreement on 29 September 2026.',
    source: '/newsroom/2026-09-30-sii-appointed-igd-authorised-distributor-in-singapore',
    sourceLabel: 'The joint press release',
    credit: 'USP Group',
  },
  {
    id: 'igd-sii-agreement-exchange',
    assetKey: 'media/igd-sii-agreement-exchange',
    entity: 'SII',
    date: '2026-09-29',
    title: 'The signed agreement, exchanged',
    alt:
      'Mr Melvin Tan Wei Yang and Mr Uu Su Haw exchanging the signed Distributor and Channel Partner Agreement folders, watched by USP Group directors and officers.',
    context:
      'IGD is the UK-based fixed gas detection manufacturer within Sweden-headquartered Safe Monitoring Group, drawing on over 100 years of gas detection engineering. The appointment marks a further step in the group’s Asia-Pacific growth strategy following its entry into the region earlier in 2026, and gives Singapore customers one route from system design and installation through to calibration, servicing and lifetime support.',
    caption:
      'Mr Melvin Tan Wei Yang, CEO of SII, and Mr Uu Su Haw, President, Asia Pacific, Safe Monitoring Group, exchange the signed agreement.',
    source: '/newsroom/2026-09-30-sii-appointed-igd-authorised-distributor-in-singapore',
    sourceLabel: 'The joint press release',
    credit: 'USP Group',
  },
  {
    id: 'sii-50th-anniversary',
    assetKey: 'media/sii-50th-anniversary',
    feature: true,
    entity: 'SII',
    date: '2025',
    title: 'Fifty years of Scientific & Industrial Instrumentation',
    alt:
      'Staff, families and guests of Scientific & Industrial Instrumentation gathered under a chandelier at York Hotel, Singapore, in front of a screen reading “Celebrating 50 years of Science”.',
    context:
      'Scientific & Industrial Instrumentation Pte. Ltd. (SII) was founded in Singapore in 1975 — the decade the country first set its course toward becoming Southeast Asia’s manufacturing and life-sciences hub. From the start, our purpose has been to give the region’s most demanding industries the measurement, calibration, and gas-safety capability they need to operate to international standard.',
    caption: 'SII’s 50th anniversary celebration, Singapore.',
    source: 'https://sii.asia/about',
    sourceLabel: 'sii.asia/about',
    credit: 'USP Group',
  },
  {
    id: 'kimera-jv-signing',
    assetKey: 'media/kimera-jv-signing',
    entity: 'SII',
    date: '2026-08-26',
    title: 'The Kimera Health joint venture, signed in Seoul',
    alt:
      'The SII and Kimera Health delegations photographed together in a hotel lobby in Seoul on the day the joint venture and commercial collaboration agreement was signed.',
    context:
      'SII and the three Kimera Health companies signed their joint venture and commercial collaboration agreement at CPHI Korea 2026 in Seoul on 26 August 2026. The agreement covers joint marketing, distribution, registration and servicing across Singapore, Malaysia and Indonesia — including the Group’s IQAir clean air range in Singapore and Indonesia. It is unincorporated: no new company, no capital contribution and no consideration.',
    caption: 'The two delegations in Seoul, 26 August 2026.',
    source: '/investor-relations/announcements/2026-08-26-entry-into-a-joint-venture-and-letter-of-intent-with-kimera-health-group',
    sourceLabel: 'The SGXNet announcement',
    credit: 'USP Group',
  },
  {
    id: 'supratechnic-service-team',
    assetKey: 'facility/supratechnic-service-team',
    entity: 'Supratechnic',
    title: 'The Supratechnic workshop and service centre',
    alt:
      'Three Supratechnic service engineers in branded uniforms standing beside a Supratechnic service van outside the workshop and service centre.',
    context:
      'Sales is the front of the marine business; service is what keeps it. The workshop and service centre carries spare parts and the engineers who fit them, so a customer who buys an outboard has somewhere to bring it.',
    caption: 'Service engineers and the mobile service unit.',
    source: 'https://www.supratechnic.com/',
    sourceLabel: 'supratechnic.com',
    credit: 'Supratechnic Pte Ltd',
  },
  {
    id: 'sii-engineer-calibration',
    assetKey: 'facility/industrial-site-1',
    entity: 'SII',
    title: 'Calibration in progress',
    alt:
      'An SII engineer in a white laboratory coat connecting tubing to a calibration instrument on a laboratory bench.',
    context:
      'SIICAL is an in-house laboratory, not an outsourced arrangement. Instruments are calibrated by SII engineers against reference standards the Group owns and maintains, which is what makes the certificate defensible when an auditor asks.',
    caption: 'An engineer calibrating a customer instrument.',
    source: 'https://sii.asia/services',
    sourceLabel: 'sii.asia/services',
    credit: 'Scientific & Industrial Instrumentation Pte Ltd',
  },
  {
    id: 'sii-gas-calibration-on-site',
    assetKey: 'facility/gas-calibration-on-site',
    entity: 'SII',
    title: 'Gas detection calibrated where it is installed',
    alt:
      'An SII technician in a high-visibility vest applying test gas to a fixed gas detector mounted on a plant wall.',
    context:
      'Fixed gas detection has to be proven in place. The team applies test gas to each head on site, records the response, and certifies the installation — across pharmaceutical, semiconductor and oil and gas facilities in the region.',
    caption: 'Bump-testing a fixed detector head on site.',
    source: 'https://sii.asia/services',
    sourceLabel: 'sii.asia/services',
    credit: 'Scientific & Industrial Instrumentation Pte Ltd',
  },
  {
    id: 'sii-co2-monitoring',
    assetKey: 'facility/beverage-co2-site',
    entity: 'SII',
    title: 'CO₂ safety monitoring',
    alt:
      'An engineer in a laboratory coat working on carbon dioxide monitoring equipment at a beverage facility.',
    context:
      'When CO₂ leaks, people are hurt quickly. SII is LogiCO₂’s appointed regional partner and installs, commissions, calibrates and services the platform in breweries, hotel basements, kitchens, hospitals and schools.',
    caption: 'LogiCO₂ monitoring at a beverage and hospitality site.',
    source: 'https://sii.asia/products',
    sourceLabel: 'sii.asia/products',
    credit: 'Scientific & Industrial Instrumentation Pte Ltd',
  },
  {
    id: 'sii-thermal-validation',
    assetKey: 'facility/industrial-site-2',
    entity: 'SII',
    title: 'Thermal validation systems',
    alt:
      'A validation system display showing temperature sensor channels during a thermal mapping run.',
    context:
      'Ellab and Kaye validation systems — Validator 2000, E-Val Pro, TrackSense Pro — supplied, commissioned and serviced for pharmaceutical, biotech, medical device and food manufacturers across the region.',
    caption: 'A thermal mapping run under way.',
    source: 'https://sii.asia/services',
    sourceLabel: 'sii.asia/services',
    credit: 'Scientific & Industrial Instrumentation Pte Ltd',
  },
  {
    id: 'siical-reference-chamber',
    assetKey: 'facility/kambic-kk105ch-chamber',
    entity: 'SII',
    title: 'Reference standards at SIICAL',
    alt: 'A Kambic KK-105 CH temperature and humidity chamber in the SIICAL calibration laboratory.',
    context:
      'The Kambic KK-105 CH chamber, alongside SPRTs, the Fluke Super-Thermometer, Humor 20 and a Thunder Scientific 2500 generator, is the reference level the laboratory calibrates against.',
    caption: 'The Kambic KK-105 CH temperature and humidity chamber.',
    source: 'https://sii.asia/about',
    sourceLabel: 'sii.asia/about',
    credit: 'Scientific & Industrial Instrumentation Pte Ltd',
  },
  {
    id: 'suprayi-iqair-atem-x',
    assetKey: 'facility/iqair-atem-x-living-room',
    entity: 'SupraYi',
    title: 'IQAir in the home',
    alt: 'An IQAir Atem X air purifier installed in a residential living room.',
    context:
      'SupraYi is the Group’s direct-to-customer clean air business. Supratechnic holds the IQAir distribution appointment for Singapore, and the same clinical-grade filtration sold into hospitals is sold to households.',
    caption: 'The IQAir Atem X in a Singapore home.',
    source: 'https://suprayi.com/',
    sourceLabel: 'suprayi.com',
    credit: 'SupraYi',
  },
];

export const FEATURED_MEDIA = MEDIA_GALLERY.find((item) => item.feature);
export const MEDIA_GRID = MEDIA_GALLERY.filter((item) => !item.feature);

/**
 * The CEO in the market.
 *
 * Posts published by the Chief Executive Officer on LinkedIn, newest first. The
 * excerpt is his own wording, trimmed — not a paraphrase. `assetKey` is null
 * where the post carried no image of its own, and those entries render as text.
 */
export const LEADERSHIP_POSTS = [
  {
    id: 'digital-ir-masterclass',
    assetKey: 'media/digital-ir-masterclass',
    date: '2026-08-12',
    title: 'Speaking at the Digital Investor Relations Masterclass',
    alt:
      'Promotional graphic for the Digital Investor Relations Masterclass reading “Is the market recognising your company’s full value?”, with SGX as strategic partner.',
    excerpt:
      'Strong performance does not always translate into strong market recognition — and I have lived that reality from the listed-company side. Closing the gap between what management knows and what the market understands is not a communications exercise. It is a discipline of trust, consistency and disclosure done right.',
    url: 'https://www.linkedin.com/posts/melvintwy_strong-performance-does-not-always-translate-activity-7493225876734701568-Cd7Q',
  },
  {
    id: 'black-sun-sti30-panel',
    assetKey: 'media/black-sun-sti30-report',
    date: '2026-07-29',
    title: 'The investor voice on the STI30 reporting panel',
    alt:
      'Copies of the Black Sun Asia Pacific STI30 reporting and communications research laid out on a table at the SGX Auditorium.',
    excerpt:
      'One finding stayed with me: 97% of STI30 companies link their strategy to market conditions, but only 20% connect those same conditions to their principal risks. When a company doesn’t explain itself well, institutions simply walk away. Retail investors don’t always have that discipline.',
    url: 'https://www.linkedin.com/posts/melvintwy_today-black-sun-asia-pacific-proudly-launched-activity-7488176430925873153-0kD-',
  },
  {
    id: 'sti30-speaker-spotlight',
    assetKey: 'media/sti30-speaker-spotlight',
    date: '2026-07-09',
    title: 'Panellist at Unlocking Value Through Trust',
    alt:
      'Speaker spotlight graphic featuring Melvin Tan as panellist for the Black Sun Global and SIAS STI30 research launch.',
    excerpt:
      'Trust isn’t built at the AGM — it’s built in every disclosure, every report, and every conversation with shareholders in between.',
    url: 'https://www.linkedin.com/posts/melvintwy_corporatecommunications-investors-investorrelations-activity-7480913816285892608-pW7R',
  },
  {
    id: 'suprayi-iqair-launch',
    assetKey: 'media/zaobao-iqair-suprayi',
    date: '2026-05-06',
    title: 'SupraYi launches as the IQAir channel for Singapore',
    alt:
      'A Lianhe Zaobao newspaper page reporting Supratechnic’s sole Singapore distribution rights for IQAir and the launch of the SupraYi e-commerce platform.',
    excerpt:
      'Supra. Yi. The first part of our name reflects excellence above the ordinary. The second — yi (医) — is the Chinese character for healing and medicine. Today, SupraYi launches as the official e-commerce and advisory channel for IQAir in Singapore, alongside Supratechnic’s appointment as sole authorised nationwide distributor.',
    url: 'https://www.linkedin.com/feed/update/urn:li:activity:7457785580849053696/',
  },
  {
    id: 'custody-reform',
    assetKey: 'media/usp-egm-auditorium',
    date: '2026-03-20',
    title: 'From the courtroom to the boardroom: custody reform and the Companies Act',
    alt:
      'Melvin Tan seated alone in an auditorium in front of a screen reading “Extraordinary General Meeting of USP Group Limited”.',
    excerpt:
      'The judge said I was right. He said the law was broken. Then he ruled against me anyway. That is what minority shareholder activism looks like in Singapore today — and why the call for custody reform and Companies Act amendments deserves more than polite agreement. It deserves urgency.',
    url: 'https://www.linkedin.com/feed/update/urn:li:activity:7440570328763834368/',
  },
  {
    id: 'hidea-appointment',
    assetKey: 'media/hidea-appointment-handshake',
    date: '2025-07-01',
    title: 'Appointed exclusive distributor for Hidea Outboards',
    alt:
      'Melvin Tan Wei Yang, Chief Executive Officer of Supratechnic (M) Sdn. Bhd., shaking hands with Zheng Huali, Founder and Executive Chairman of Hidea Power Machinery Co., Ltd.',
    excerpt:
      'We’ve been appointed the exclusive distributor for Hidea Outboards in Malaysia and Singapore, effective 1 July 2025. When I first visited Hidea’s facility, I was struck by their engineering precision, automation, and ambition to go global. This is not just about selling engines. It’s about redefining how the region experiences marine mobility.',
    url: 'https://www.linkedin.com/feed/update/urn:li:activity:7345722818107494401/',
  },
  {
    id: 'sias-ai-governance',
    assetKey: null,
    date: '2025-05-08',
    title: 'Featured by SIAS on corporate governance in the age of AI',
    excerpt:
      'As technology continues to transform the way we live and work, it’s critical we don’t lose sight of the human element — values, accountability, and trust.',
    url: 'https://www.linkedin.com/feed/update/urn:li:activity:7326157635865509890/',
  },
];
